import PhoneInput from '../components/common/PhoneInput';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  listBookableServices,
  listServicesWithSessions,
  getFreeSlots,
  bookSlot,
  startBookingOtp,
  verifyBookingOtp,
  listBookableSessions,
  enrollInSession,
  fmtSessionDate,
  fmtSessionTime,
  spotsLabel,
  scheduleErrorMessage,
} from "./scheduleBookingService";

/**
 * BookingCard — the booking UI used by the public member booking page.
 *
 * One scrollable card: gym label → service radio list → (date strip + live
 * times OR group class days + sessions) → name/email confirm modal →
 * "You're booked" confirmation.
 *
 * Two booking models, picked from each service's `type`:
 *   • Free-slot (individual / space): getFreeSlots → bookSlot
 *   • Fixed-session (group): listBookableSessions → enrollInSession
 *
 * Props:
 *   orgId          — org to load services for
 *   seedServices   — published services from the slug resolve (authoritative)
 *   coachNameById  — { memberId: name } fallback for group session coach labels
 *   timezone       — IANA tz, used for display ONLY when mode="preview"
 *   mode           — "live" (real book/enroll, member's browser tz)
 *                    | "preview" (no real POST, uses `timezone`)
 *   heading        — card heading (default "Book a session")
 *   subLabel       — small teal label above the heading (default "Your gym")
 *   primaryColor   — optional organization colour; defaults to teal
 *   onDone         — optional callback after a successful booking
 */

const INK = "#111827";
const BODY = "#374151";
const MUTED = "#6B7280";
const FAINT = "#9CA3AF";


const BORDER = "#E5E7EB";
const DANGER = "#DC2626";

const iconProps = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true };

const ServiceIcons = {
  individual: (<svg {...iconProps} width={20} height={20}><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>),
  group: (<svg {...iconProps} width={20} height={20}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.9" /><path d="M16 3.1a4 4 0 0 1 0 7.8" /></svg>),
  space: (<svg {...iconProps} width={20} height={20}><path d="M11 20H2" /><path d="M11 4.562v16.157a1 1 0 0 0 1.242.97L19 20V5.562a2 2 0 0 0-1.515-1.94l-4-1A2 2 0 0 0 11 4.561z" /><path d="M11 4H8a2 2 0 0 0-2 2v14" /><path d="M14 12h.01" /><path d="M22 20h-3" /></svg>),
};
const iconKey = (type) => (type === "group" ? "group" : type === "space" ? "space" : "individual");
const isFreeSlot = (type) => type === "individual" || type === "space";
const emailValid = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());

// Normalize user-typed phone to E.164. Returns "" when it can't be made valid.
// Accepts "+<country><number>", 10-digit US numbers, and 11-digit "1…" numbers.
function normalizePhone(raw) {
  const s = (raw || "").trim();
  if (!s) return "";
  const digits = s.replace(/\D/g, "");
  let e164 = "";
  if (s.startsWith("+")) e164 = `+${digits}`;
  else if (digits.length === 10) e164 = `+1${digits}`;
  else if (digits.length === 11 && digits.startsWith("1")) e164 = `+${digits}`;
  return /^\+[1-9]\d{7,14}$/.test(e164) ? e164 : "";
}

const OTP_RESEND_COOLDOWN_MS = 30 * 1000;
const OTP_RATE_LIMIT_COOLDOWN_MS = 10 * 60 * 1000;
const fmtCountdown = (sec) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;

function serviceSubtitle(svc) {
  if (svc.type === "group") return `${svc.durationMinutes} min · small group, up to ${svc.spots}`;
  if (svc.type === "space") return `${svc.durationMinutes} min · self-guided`;
  return `${svc.durationMinutes} min · 1 on 1`;
}

// "6:30 – 7:30 PM" from a free slot's Unix-seconds start/end. Drops the first
// AM/PM when both ends share it; keeps both when they differ ("11:30 AM – 12:30 PM").
function slotRangeLabel(startSec, endSec, tz) {
  const startIso = new Date(startSec * 1000).toISOString();
  const startFull = fmtSessionTime(startIso, tz);
  if (!endSec) return startFull;
  const endIso = new Date(endSec * 1000).toISOString();
  const endFull = fmtSessionTime(endIso, tz);
  const sMerid = startFull.match(/[AP]M$/i)?.[0];
  const eMerid = endFull.match(/[AP]M$/i)?.[0];
  if (sMerid && eMerid && sMerid.toUpperCase() === eMerid.toUpperCase()) {
    return `${startFull.replace(/\s*[AP]M$/i, "")} – ${endFull}`;
  }
  return `${startFull} – ${endFull}`;
}

function upcomingDays(count) {
  const out = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i);
    out.push({ key: `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`, dow: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()], day: d.getDate(), dateObj: d });
  }
  return out;
}

// UTC offset (minutes) of a timezone at a given instant. Uses Intl, so DST is handled.
function tzOffsetMinutes(date, timeZone) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone, hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const parts = dtf.formatToParts(date).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  const asUTC = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
  return (asUTC - date.getTime()) / 60000;
}

// UTC [from, to) window covering the whole selected calendar day in `tz`.
function dayWindow(dateObj, tz) {
  const y = dateObj.getFullYear();
  const m = dateObj.getMonth();
  const d = dateObj.getDate();

  if (!tz) {
    const from = new Date(y, m, d, 0, 0, 0, 0);
    const to = new Date(from); to.setDate(to.getDate() + 1);
    return { from: from.toISOString(), to: to.toISOString() };
  }

  // Two-pass offset correction handles the DST edge where the offset differs.
  const localMidnightGuess = new Date(Date.UTC(y, m, d, 0, 0, 0));
  const off1 = tzOffsetMinutes(localMidnightGuess, tz);
  let fromMs = Date.UTC(y, m, d, 0, 0, 0) - off1 * 60000;
  const off2 = tzOffsetMinutes(new Date(fromMs), tz);
  if (off2 !== off1) fromMs = Date.UTC(y, m, d, 0, 0, 0) - off2 * 60000;

  const toMs = fromMs + 24 * 60 * 60 * 1000;
  return { from: new Date(fromMs).toISOString(), to: new Date(toMs).toISOString() };
}

const inputStyle = { height: 44, width: "100%", borderRadius: 12, border: "1px solid #D1D5DB", padding: "0 12px", fontSize: 16, fontFamily: "inherit", color: INK, outline: "none", boxSizing: "border-box" };

// Stable local-date key ("2026-09-25") for an ISO datetime, in the given tz.
function localDateKey(iso, tz) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
}
// Short "Thu" / "25" chip labels for an ISO datetime, in the given tz.
function dayChipLabel(iso, tz) {
  const dow = new Intl.DateTimeFormat(undefined, { timeZone: tz, weekday: "short" }).format(new Date(iso));
  const day = new Intl.DateTimeFormat(undefined, { timeZone: tz, day: "numeric" }).format(new Date(iso));
  return { dow, day };
}
// Bucket group sessions by local date → [{ key, dow, day, sessions }], date-sorted.
function groupSessionsByDay(sessions, tz) {
  const map = new Map();
  for (const s of sessions) {
    const key = localDateKey(s.startDatetimeUtc, tz);
    if (!map.has(key)) {
      const { dow, day } = dayChipLabel(s.startDatetimeUtc, tz);
      map.set(key, { key, dow, day, sessions: [] });
    }
    map.get(key).sessions.push(s);
  }
  return [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
}

export default function BookingCard({ orgId, seedServices = [], coachNameById = {}, timezone, mode = "live", heading = "Book a session", subLabel = "Your gym", primaryColor, onDone }) {
  const preview = mode === "preview";
  const configuredColor = typeof primaryColor === "string" ? primaryColor.trim() : "";
  const accent = configuredColor || "#115E59";
  const accentText = configuredColor || "#0F766E";
  const selectedBackground = configuredColor
    ? `color-mix(in srgb, ${configuredColor} 8%, white)`
    : "rgba(240,253,250,0.6)";
  const dates = useMemo(() => upcomingDays(7), []);

  // ── Merged service list (both models) ──
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState("");

  // Latest seedServices in a ref so loadServices doesn't change identity on every
  // render (a new [] default each render would re-run the fetch effect forever).
  const seedRef = useRef(seedServices);
  seedRef.current = seedServices;

  const loadServices = useCallback(async () => {
    if (!orgId) return;
    setServicesLoading(true);
    setServicesError("");
    try {
      const [freeRes, groupRes, sessRes] = await Promise.allSettled([
        listBookableServices(orgId),
        listServicesWithSessions(orgId),
        // Also pull all open sessions so group service cards can be rebuilt
        // even if /services-with-sessions returns them incompletely.
        listBookableSessions(orgId),
      ]);
      const free = freeRes.status === "fulfilled" ? (freeRes.value?.services || []) : [];
      const group = groupRes.status === "fulfilled" ? (groupRes.value?.services || []) : [];

      const sessions = sessRes.status === "fulfilled" ? (sessRes.value?.sessions || []) : [];
      const derivedGroup = {};
      for (const s of sessions) {
        const id = s.serviceClassId;
        if (!id || s.type !== "group") continue;
        if (!derivedGroup[id]) {
          derivedGroup[id] = { id, name: s.serviceName, type: "group", durationMinutes: s.durationMinutes, spots: s.capacity };
        }
      }

      // Seed → derived → free → group. Later entries enrich earlier ones.
      const seed = seedRef.current || [];
      const byId = {};
      [...seed, ...Object.values(derivedGroup), ...free, ...group].forEach((s) => {
        if (s?.id) byId[s.id] = { ...byId[s.id], ...s };
      });
      setServices(Object.values(byId));
      if (seed.length === 0 && freeRes.status === "rejected" && groupRes.status === "rejected" && sessRes.status === "rejected") {
        setServicesError(scheduleErrorMessage(freeRes.reason, "Couldn't load services"));
      }
    } catch (err) {
      setServicesError(scheduleErrorMessage(err, "Couldn't load services"));
    } finally {
      setServicesLoading(false);
    }
  }, [orgId]);

  useEffect(() => { loadServices(); }, [loadServices]);

  // ── Selection ──
  const [serviceId, setServiceId] = useState(null);
  const service = services.find((s) => s.id === serviceId) || null;
  const type = service?.type;

  const [dayKey, setDayKey] = useState(dates[0]?.key || null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState("");
  const [slotCoach, setSlotCoach] = useState(null); // { coachMemberId, name, email } from /slots

  const browserTz = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return undefined; }
  }, []);
  // Live mode shows every time in the member's own browser timezone (stored
  // instants are UTC). Preview mode shows the gym timezone passed via `timezone`.
  const displayTz = preview ? (timezone || browserTz) : browserTz;

  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState("");
  const [sessionDayKey, setSessionDayKey] = useState(null);

  const [selected, setSelected] = useState(null); // { kind: "slot"|"session", data }
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [booked, setBooked] = useState(false);
  const [notice, setNotice] = useState(""); // e.g. "slot no longer available"

  // ── Phone OTP (free-slot bookings, live mode only) ──
  // idle → sent → verified → (expired | booked). bookingToken lives in memory only.
  const [phone, setPhone] = useState("");
  const [otpStatus, setOtpStatus] = useState("idle"); // idle | sent | verified | expired
  const [otpPhone, setOtpPhone] = useState(""); // E.164 phone the code was sent to
  const [otpCode, setOtpCode] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [bookingToken, setBookingToken] = useState(null);
  const [tokenTtlMs, setTokenTtlMs] = useState(0);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const resetOtp = useCallback((status = "idle") => {
    setBookingToken(null); setTokenTtlMs(0);
    setOtpStatus(status); setOtpCode(""); setOtpError(""); setOtpPhone("");
  }, []);

  // Tick once a second while a resend / rate-limit cooldown is running.
  useEffect(() => {
    if (cooldownUntil <= Date.now()) return undefined;
    setNowMs(Date.now());
    const t = setInterval(() => {
      const n = Date.now();
      setNowMs(n);
      if (n >= cooldownUntil) clearInterval(t);
    }, 1000);
    return () => clearInterval(t);
  }, [cooldownUntil]);
  const cooldownLeft = Math.max(0, Math.ceil((cooldownUntil - nowMs) / 1000));

  // Drop the token when its TTL elapses so the booker must re-verify.
  useEffect(() => {
    if (!bookingToken || !tokenTtlMs) return undefined;
    const t = setTimeout(() => resetOtp("expired"), tokenTtlMs);
    return () => clearTimeout(t);
  }, [bookingToken, tokenTtlMs, resetOtp]);

  const loadSlots = useCallback(async (svcId, picked) => {
    if (!svcId || !picked) return;
    setSlotsLoading(true); setSlotsError(""); setSlots([]);
    try {
      // Query the full selected day in the same tz the slot times are shown in,
      // otherwise early/late slots get clipped after conversion.
      const { from, to } = dayWindow(picked.dateObj, displayTz);
      const res = await getFreeSlots(svcId, { from, to });
      setSlots(res?.slots || []);
      if (res?.coach) setSlotCoach(res.coach);
    } catch (err) {
      setSlotsError(scheduleErrorMessage(err, "Couldn't load available times"));
    } finally {
      setSlotsLoading(false);
    }
  }, [displayTz]);

  const loadSessions = useCallback(async (svcId) => {
    if (!orgId || !svcId) return;
    setSessionsLoading(true); setSessionsError(""); setSessions([]); setSessionDayKey(null);
    try {
      const res = await listBookableSessions(orgId, svcId);
      const list = res?.sessions || [];
      setSessions(list);
      if (list.length) {
        const groups = groupSessionsByDay(list, displayTz);
        setSessionDayKey(groups[0]?.key || null);
      }
    } catch (err) {
      setSessionsError(scheduleErrorMessage(err, "Couldn't load available times"));
    } finally {
      setSessionsLoading(false);
    }
  }, [orgId, displayTz]);

  const selectService = (svc) => {
    setServiceId(svc.id);
    setSelected(null); setName(""); setEmail(""); setPhone(""); setSubmitError(""); setBooked(false);
    setNotice("");
    resetOtp("idle");
    setSlotCoach(null);
    if (isFreeSlot(svc.type)) {
      const d = dates[0];
      setDayKey(d?.key || null);
      loadSlots(svc.id, d);
    } else {
      loadSessions(svc.id);
    }
  };

  // Auto-select the first service once the list loads.
  useEffect(() => {
    if (!serviceId && services.length > 0) selectService(services[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services]);

  const pickDay = (d) => { setDayKey(d.key); setSelected(null); setNotice(""); loadSlots(serviceId, d); };

  const summaryLine = useMemo(() => {
    if (!service || !selected) return "";
    if (selected.kind === "slot") {
      const iso = new Date(selected.data.start_time * 1000).toISOString();
      const range = slotRangeLabel(selected.data.start_time, selected.data.end_time, displayTz);
      return `${service.name}, ${fmtSessionDate(iso, displayTz)} at ${range}`;
    }
    const sessionName = selected.data.className?.trim() || selected.data.displayName?.trim() || selected.data.serviceName || service.name;
    return `${sessionName}, ${fmtSessionDate(selected.data.startDatetimeUtc, displayTz)} at ${fmtSessionTime(selected.data.startDatetimeUtc, displayTz)}`;
  }, [service, selected, displayTz]);

  const resetToList = () => {
    setBooked(false); setSelected(null); setName(""); setEmail(""); setPhone("");
    setSubmitError(""); setNotice(""); resetOtp("idle");
  };

  // Close the confirm modal and clear the per-booking verification state so a
  // held token never carries over to a different slot.
  const closeModal = () => {
    setSelected(null); setSubmitError(""); resetOtp("idle");
  };

  // All live bookings — free-slot and group session alike — require a verified
  // phone (see OTP guide, Step 4b). Preview mode does not.
  const otpRequired = !preview && !!selected;
  const normalizedPhone = normalizePhone(phone);
  const phoneValid = !!normalizedPhone;
  // Editing the phone after verifying invalidates the held token.
  const tokenMatchesPhone = otpStatus === "verified" && normalizedPhone === otpPhone;
  const detailsReady = name.trim() && emailValid(email) && phoneValid;

  // Step 2 — send (or resend) the SMS OTP to the booker's phone.
  const sendOtp = async () => {
    if (!phoneValid) { setOtpError("Enter a valid phone number with country code."); return; }
    if (cooldownLeft > 0 || otpBusy) return;
    setOtpBusy(true); setOtpError("");
    try {
      await startBookingOtp(normalizedPhone);
      setOtpPhone(normalizedPhone);
      setOtpStatus("sent");
      setOtpCode("");
      setBookingToken(null); setTokenTtlMs(0);
      setCooldownUntil(Date.now() + OTP_RESEND_COOLDOWN_MS);
    } catch (err) {
      if (err?.response?.status === 429) {
        setCooldownUntil(Date.now() + OTP_RATE_LIMIT_COOLDOWN_MS);
      }
      setOtpError(scheduleErrorMessage(err, "Couldn't send the code. Try again."));
    } finally {
      setOtpBusy(false);
    }
  };

  // Step 3 — verify the code → hold the booking token + start its TTL timer.
  const verifyOtp = async () => {
    const code = otpCode.trim();
    if (code.length < 4 || otpBusy) return;
    setOtpBusy(true); setOtpError("");
    try {
      const res = await verifyBookingOtp(otpPhone, code);
      const token = res?.bookingToken;
      if (!token) throw new Error("Verification failed. Try again.");
      setBookingToken(token);
      setTokenTtlMs(Math.max(0, (res?.expiresIn ?? 600) * 1000));
      setOtpStatus("verified");
      setOtpError("");
    } catch (err) {
      setOtpError(scheduleErrorMessage(err, "Invalid or expired code."));
    } finally {
      setOtpBusy(false);
    }
  };

  const submit = async () => {
    if (!selected) return;
    if (!name.trim()) { setSubmitError("Enter your name."); return; }
    if (!emailValid(email)) { setSubmitError("Enter a valid email address."); return; }

    // Preview mode: no API call, just show the confirmation.
    if (preview) { setBooked(true); return; }

    // Both models now require a verified phone that still matches the token.
    if (!phoneValid) { setSubmitError("Enter a valid phone number."); return; }
    if (otpStatus !== "verified" || !bookingToken) { setSubmitError("Verify your phone to continue."); return; }
    if (!tokenMatchesPhone) { resetOtp("idle"); setSubmitError("Phone changed — please verify again."); return; }

    setSubmitting(true);
    setSubmitError("");
    const member = { name: name.trim(), email: email.trim(), phone: otpPhone };
    try {
      if (selected.kind === "slot") {
        const startIso = new Date(selected.data.start_time * 1000).toISOString();
        await bookSlot(serviceId, { startIso, bookingToken, member });
      } else {
        await enrollInSession(selected.data.id, { bookingToken, member });
      }
      setBooked(true);
      onDone?.();
    } catch (err) {
      const status = err?.response?.status;
      const message = scheduleErrorMessage(err, "Couldn't complete your booking");
      if (status === 409) {
        // Slot/session taken meanwhile — keep the token (still valid within its
        // TTL), close the modal, and bounce back to the relevant picker.
        const wasSlot = selected.kind === "slot";
        setSelected(null);
        setNotice(message || (wasSlot
          ? "That time is no longer available. Please pick another slot."
          : "That session is no longer available. Please pick another."));
        if (wasSlot) {
          const picked = dates.find((d) => d.key === dayKey);
          if (picked) loadSlots(serviceId, picked);
        } else {
          loadSessions(serviceId);
        }
      } else if (status === 401) {
        // Token missing / expired / phone mismatch → must re-verify.
        resetOtp("idle");
        setSubmitError(message || "Phone verification required. Please verify again.");
      } else {
        setSubmitError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Booked confirmation ──
  if (booked) {
    return (
      <div style={{ padding: "24px 0", textAlign: "center" }}>
        <span style={{ margin: "0 auto", display: "flex", width: 48, height: 48, alignItems: "center", justifyContent: "center", borderRadius: 999, background: "#ECFDF5", color: "#059669" }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width={24} height={24}><path d="M20 6 9 17l-5-5" /></svg>
        </span>
        <h1 style={{ margin: "16px 0 0", fontSize: 20, fontWeight: 600, color: INK }}>You&apos;re booked</h1>
        <p style={{ margin: "4px 0 0", fontSize: 14, color: BODY }}>{summaryLine}</p>
        {preview
          ? <p style={{ margin: "8px 0 0", fontSize: 12, color: FAINT, fontStyle: "italic" }}>(Preview — no real booking was made)</p>
          : <p style={{ margin: "8px 0 0", fontSize: 12.5, color: FAINT }}>A confirmation is on its way. Your calendar app may send its own invite.</p>}
        <div style={{ marginTop: 20, display: "flex", justifyContent: "center", gap: 8 }}>
          <button type="button" onClick={resetToList} style={{ display: "inline-flex", alignItems: "center", height: 40, padding: "0 16px", borderRadius: 999, border: "1px solid #D1D5DB", background: "#fff", fontFamily: "inherit", fontSize: 14, fontWeight: 500, color: "#1F2937", cursor: "pointer" }}>Book another</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <p style={{ margin: 0, fontSize: 12, fontWeight: 500, color: accentText }}>{subLabel}</p>
      <h1 style={{ margin: "2px 0 0", fontSize: 20, fontWeight: 600, color: INK }}>{heading}</h1>

      {/* Service radio list */}
      <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }} role="radiogroup" aria-label="Service">
        {servicesLoading && [1, 2].map((i) => <div key={i} style={{ height: 64, borderRadius: 16, background: "#F3F4F6" }} />)}
        {!servicesLoading && servicesError && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <p style={{ margin: 0, fontSize: 14, color: DANGER }}>{servicesError}</p>
            <button type="button" onClick={loadServices} style={{ fontFamily: "inherit", fontSize: 13, color: MUTED, background: "transparent", border: "1px solid #D1D5DB", borderRadius: 999, padding: "6px 12px", cursor: "pointer" }}>Retry</button>
          </div>
        )}
        {!servicesLoading && !servicesError && services.length === 0 && <p style={{ margin: 0, fontSize: 14, color: MUTED }}>No bookable services right now. Check back soon.</p>}
        {!servicesLoading && !servicesError && services.map((s) => {
          const sel = s.id === serviceId;
          return (
            <button key={s.id} type="button" role="radio" aria-checked={sel} onClick={() => selectService(s)} style={{ display: "flex", width: "100%", alignItems: "center", gap: 12, borderRadius: 16, padding: 12, textAlign: "left", cursor: "pointer", fontFamily: "inherit", border: `1px solid ${sel ? accentText : BORDER}`, background: sel ? selectedBackground : "#fff", boxShadow: sel ? `0 0 0 1px ${accentText}` : "none" }}>
              <span style={{ display: "inline-flex", flex: "none", width: 40, height: 40, alignItems: "center", justifyContent: "center", borderRadius: 12, background: sel ? accent : "#F9FAFB", color: sel ? "#fff" : "#4B5563" }}>{ServiceIcons[iconKey(s.type)]}</span>
              <span>
                <span style={{ display: "block", fontWeight: 500, color: INK }}>{s.name}</span>
                <span style={{ display: "block", fontSize: 12, color: MUTED }}>{serviceSubtitle(s)}</span>
              </span>
            </button>
          );
        })}
      </div>

      {/* FREE-SLOT: date strip + times */}
      {serviceId && isFreeSlot(type) && (
        <>
          <p style={{ margin: "16px 0 0", fontSize: 13, fontWeight: 500, color: BODY }}>Available times</p>
          {notice && (
            <div style={{ marginTop: 10, fontSize: 13.5, color: "#92400E", padding: "10px 12px", borderRadius: 12, background: "#FFFBEB", border: "1px solid #FDE68A" }}>{notice}</div>
          )}
          <div style={{ marginTop: 10, display: "flex", gap: 4 }}>
            {dates.map((d) => {
              const activeDay = d.key === dayKey;
              return (
                <button key={d.key} type="button" aria-pressed={activeDay} onClick={() => pickDay(d)} style={{ flex: 1, borderRadius: 12, padding: "8px 0", textAlign: "center", border: "none", cursor: "pointer", fontFamily: "inherit", background: activeDay ? accent : "transparent", color: activeDay ? "#fff" : BODY }}>
                  <span style={{ display: "block", fontSize: 11 }}>{d.dow}</span>
                  <span style={{ display: "block", fontSize: 16, fontWeight: 600 }}>{d.day}</span>
                </button>
              );
            })}
          </div>
          <p style={{ margin: "8px 0 0", fontSize: 12, color: FAINT }}>Times shown in {displayTz || "your local time"}</p>
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            {slotsLoading && [1, 2, 3].map((i) => <div key={i} style={{ height: 48, borderRadius: 16, background: "#F3F4F6" }} />)}
            {!slotsLoading && slotsError && <p style={{ margin: 0, fontSize: 14, color: DANGER }}>{slotsError}</p>}
            {!slotsLoading && !slotsError && slots.length === 0 && <p style={{ margin: 0, fontSize: 14, color: MUTED }}>No times available this day. Try another day.</p>}
            {!slotsLoading && !slotsError && slots.map((s) => {
              const active = selected?.kind === "slot" && selected.data.start_time === s.start_time;
              return (
                <button key={s.start_time} type="button" onClick={() => { setSelected({ kind: "slot", data: s }); setNotice(""); }} style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "12px 16px", borderRadius: 16, fontFamily: "inherit", cursor: "pointer", textAlign: "left", background: active ? selectedBackground : "#fff", border: `1px solid ${active ? accentText : BORDER}`, boxShadow: active ? `0 0 0 1px ${accentText}` : "none" }}>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 16, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: INK }}>{slotRangeLabel(s.start_time, s.end_time, displayTz)}</span>
                  {slotCoach?.name && <span style={{ flex: "0 0 auto", fontSize: 13, color: MUTED, whiteSpace: "nowrap" }}>with {slotCoach.name}</span>}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* GROUP: day strip + fixed sessions for the selected day */}
      {serviceId && type === "group" && (() => {
        const groupTz = displayTz;
        const dayGroups = groupSessionsByDay(sessions, groupTz);
        const activeDay = dayGroups.find((g) => g.key === sessionDayKey) || dayGroups[0] || null;
        const daySessions = activeDay?.sessions || [];
        return (
          <>
            {notice && (
              <div style={{ marginTop: 16, fontSize: 13.5, color: "#92400E", padding: "10px 12px", borderRadius: 12, background: "#FFFBEB", border: "1px solid #FDE68A" }}>{notice}</div>
            )}
            {!sessionsLoading && !sessionsError && dayGroups.length > 0 && (
              <div style={{ marginTop: 20, display: "flex", gap: 4, overflowX: "auto" }}>
                {dayGroups.map((g) => {
                  const on = g.key === (sessionDayKey || activeDay?.key);
                  return (
                    <button key={g.key} type="button" aria-pressed={on} onClick={() => { setSessionDayKey(g.key); setSelected(null); setNotice(""); }} style={{ flex: "1 0 auto", minWidth: 52, borderRadius: 12, padding: "8px 10px", textAlign: "center", border: "none", cursor: "pointer", fontFamily: "inherit", background: on ? accent : "transparent", color: on ? "#fff" : BODY }}>
                      <span style={{ display: "block", fontSize: 11 }}>{g.dow}</span>
                      <span style={{ display: "block", fontSize: 16, fontWeight: 600 }}>{g.day}</span>
                    </button>
                  );
                })}
              </div>
            )}
            <p style={{ margin: "8px 0 0", fontSize: 12, color: FAINT }}>Times shown in {groupTz || "your local time"}</p>

            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
              {sessionsLoading && [1, 2, 3].map((i) => <div key={i} style={{ height: 52, borderRadius: 16, background: "#F3F4F6" }} />)}
              {!sessionsLoading && sessionsError && <p style={{ margin: 0, fontSize: 14, color: DANGER }}>{sessionsError}</p>}
              {!sessionsLoading && !sessionsError && sessions.length === 0 && <p style={{ margin: 0, fontSize: 14, color: MUTED }}>No upcoming times right now.</p>}
              {!sessionsLoading && !sessionsError && sessions.length > 0 && daySessions.length === 0 && <p style={{ margin: 0, fontSize: 14, color: MUTED }}>No classes this day. Pick another day.</p>}
              {!sessionsLoading && !sessionsError && daySessions.map((s) => {
                const active = selected?.kind === "session" && selected.data.id === s.id;
                const coachName = s.coachName || (s.coachMemberId ? (coachNameById?.[String(s.coachMemberId)] || "") : "");
                return (
                  <button key={s.id} type="button" disabled={s.isFull} onClick={() => { setSelected({ kind: "session", data: s }); setNotice(""); }} style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "12px 16px", borderRadius: 16, fontFamily: "inherit", cursor: s.isFull ? "not-allowed" : "pointer", opacity: s.isFull ? 0.5 : 1, textAlign: "left", background: active ? selectedBackground : "#fff", border: `1px solid ${active ? accentText : BORDER}`, boxShadow: active ? `0 0 0 1px ${accentText}` : "none" }}>
                    <span style={{ width: 80, flex: "0 0 auto", fontSize: 16, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: INK }}>{fmtSessionTime(s.startDatetimeUtc, groupTz)}</span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: MUTED }}>
                      <span style={{ display: "block", fontSize: 14, fontWeight: 600, color: INK }}>
                        {s.className?.trim() || s.displayName?.trim() || s.serviceName || service.name}
                      </span>
                      {coachName ? `with ${coachName}` : ""}
                    </span>
                    <span style={{ flex: "0 0 auto", fontSize: 13, color: MUTED }}>{spotsLabel(s)}</span>
                  </button>
                );
              })}
            </div>
          </>
        );
      })()}

      {/* Confirm modal — opens after a slot/session is picked. */}
      {selected && (
        <div
          onClick={submitting ? undefined : closeModal}
          style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, background: "rgba(17,24,39,0.4)" }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Confirm booking"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            style={{ width: "100%", maxWidth: 400, borderRadius: 24, background: "#fff", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)", padding: 24 }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
              <p style={{ margin: 0, fontWeight: 600, fontSize: 16, color: INK }}>{summaryLine}</p>
              <button
                type="button"
                aria-label="Cancel"
                onClick={closeModal}
                disabled={submitting}
                style={{ display: "inline-flex", flex: "none", width: 28, height: 28, alignItems: "center", justifyContent: "center", borderRadius: 999, border: "none", background: "transparent", color: FAINT, cursor: submitting ? "not-allowed" : "pointer" }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={18} height={18} aria-hidden="true"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
              </button>
            </div>
            <input placeholder="Jordan Lee" aria-label="Your name" value={name} onChange={(e) => setName(e.target.value)} style={{ ...inputStyle, marginTop: 16 }} autoComplete="name" />
            <input placeholder="jordan@email.com" aria-label="Your email" value={email} onChange={(e) => setEmail(e.target.value)} style={{ ...inputStyle, marginTop: 8 }} autoComplete="email" type="email" />
            {/* Phone — required and verified for free-slot bookings. */}
            <PhoneInput
              placeholder="Phone number"
              aria-label="Your phone"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                // Editing the phone after verifying invalidates the held token.
                if (otpStatus !== "idle") resetOtp("idle");
              }}
              style={{ ...inputStyle, marginTop: 8 }}
              autoComplete="tel"
              type="tel"
              inputMode="tel"
            />

            {/* OTP step — free-slot bookings in live mode only. */}
            {otpRequired && (
              <div style={{ marginTop: 10 }}>
                {otpStatus !== "verified" ? (
                  <>
                    {otpStatus === "sent" && (
                      <input
                        placeholder="Enter the 6-digit code"
                        aria-label="SMS verification code"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        style={{ ...inputStyle, letterSpacing: 4, fontVariantNumeric: "tabular-nums" }}
                        autoComplete="one-time-code"
                        inputMode="numeric"
                      />
                    )}
                    {otpStatus === "expired" && (
                      <p style={{ margin: "0 0 8px", fontSize: 13, color: "#92400E" }}>Verification expired — resend the code.</p>
                    )}
                    <div style={{ display: "flex", gap: 8, marginTop: otpStatus === "sent" ? 8 : 0 }}>
                      <button
                        type="button"
                        onClick={sendOtp}
                        disabled={!detailsReady || otpBusy || cooldownLeft > 0}
                        style={{ flex: 1, height: 44, borderRadius: 999, border: `1px solid ${accentText}`, background: "#fff", fontFamily: "inherit", fontSize: 14, fontWeight: 600, color: accentText, cursor: !detailsReady || otpBusy || cooldownLeft > 0 ? "not-allowed" : "pointer", opacity: !detailsReady || otpBusy || cooldownLeft > 0 ? 0.6 : 1 }}
                      >
                        {otpBusy && otpStatus !== "sent" ? "Sending…" : cooldownLeft > 0 ? `Resend in ${fmtCountdown(cooldownLeft)}` : otpStatus === "sent" ? "Resend code" : "Send code"}
                      </button>
                      {otpStatus === "sent" && (
                        <button
                          type="button"
                          onClick={verifyOtp}
                          disabled={otpCode.trim().length < 4 || otpBusy}
                          style={{ flex: 1, height: 44, borderRadius: 999, border: "none", background: accent, fontFamily: "inherit", fontSize: 14, fontWeight: 600, color: "#fff", cursor: otpCode.trim().length < 4 || otpBusy ? "not-allowed" : "pointer", opacity: otpCode.trim().length < 4 || otpBusy ? 0.6 : 1 }}
                        >
                          {otpBusy ? "Verifying…" : "Verify"}
                        </button>
                      )}
                    </div>
                    {otpError && (
                      <div style={{ marginTop: 8, fontSize: 13.5, color: DANGER, padding: "10px 12px", borderRadius: 12, background: "#FEF2F2", border: "1px solid #FECACA" }}>{otpError}</div>
                    )}
                  </>
                ) : (
                  <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, fontSize: 13.5, fontWeight: 500, color: "#059669" }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width={16} height={16} aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
                    Phone verified
                  </p>
                )}
              </div>
            )}

            {submitError && (
              <div style={{ marginTop: 10, fontSize: 13.5, color: DANGER, padding: "10px 12px", borderRadius: 12, background: "#FEF2F2", border: "1px solid #FECACA" }}>{submitError}</div>
            )}
            <button
              type="button"
              onClick={submit}
              disabled={submitting || !detailsReady || (otpRequired && !tokenMatchesPhone)}
              style={{ marginTop: 12, display: "inline-flex", alignItems: "center", justifyContent: "center", width: "100%", height: 44, padding: "0 16px", borderRadius: 999, border: "none", fontFamily: "inherit", fontSize: 15, fontWeight: 600, color: "#fff", background: accent, cursor: submitting || !detailsReady || (otpRequired && !tokenMatchesPhone) ? "not-allowed" : "pointer", opacity: submitting || !detailsReady || (otpRequired && !tokenMatchesPhone) ? 0.6 : 1 }}
            >
              {submitting ? "Booking…" : otpRequired ? "Confirm booking" : "Book spot"}
            </button>
            {preview && <p style={{ margin: "8px 0 0", fontSize: 11, color: FAINT, textAlign: "center", fontStyle: "italic" }}>Preview only — no real booking is made</p>}
          </div>
        </div>
      )}
    </>
  );
}
